import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { ReplaceAssignmentBody, ReplaceAssignmentResponse } from '@cast/types';

import { getCommitmentsAssignmentsListQueryKey } from 'hooks/queries/commitments/useAssignmentsQuery';
import { getCommitmentAssignmentsQueryKey } from 'hooks/queries/commitments/useCommitmentAssignmentsQuery';

class ReplaceAssignmentError extends Error {
  constructor(
    public readonly assignment: ReplaceAssignmentBody,
    public readonly error: AxiosError
  ) {
    super(`Failed to replace assignment. Error: ${error.message}`);
  }
}

class ReplaceAssignmentsError extends Error {
  constructor(
    public readonly success: ReplaceAssignmentResponse[],
    public readonly errors: [
      ReplaceAssignmentError,
      ...ReplaceAssignmentError[]
    ]
  ) {
    super(`Failed to replace ${pluralize('assignment', errors.length)}.`);
  }
}

const replaceAssignment = async (assignment: ReplaceAssignmentBody) => {
  return await apiClient.commitments
    .replaceAssignments(assignment)
    .then((r) => r.data)
    .catch(
      (error: AxiosError) => new ReplaceAssignmentError(assignment, error)
    );
};

const replaceAssignments = async (assignments: ReplaceAssignmentBody[]) => {
  return Promise.all(assignments.map(replaceAssignment));
};

export const useReplaceAssignmentsMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignments: ReplaceAssignmentBody[]) => {
      const result = await replaceAssignments(assignments);
      const errors = result.filter(
        (r: ReplaceAssignmentResponse | ReplaceAssignmentError) =>
          r instanceof ReplaceAssignmentError
      );

      if (errors.length) {
        const success = result.filter(
          (r: ReplaceAssignmentResponse | ReplaceAssignmentError) =>
            !(r instanceof ReplaceAssignmentError)
        );
        throw new ReplaceAssignmentsError(
          success as ReplaceAssignmentResponse[],
          errors as [ReplaceAssignmentError, ...ReplaceAssignmentError[]]
        );
      }
      return result as ReplaceAssignmentResponse[];
    },
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({
        queryKey: getCommitmentsAssignmentsListQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getCommitmentAssignmentsQueryKey(),
      });
    },
    onError: (error) => {
      notify.error(parseApiError(error));
    },
  });
};
